
    import Vue from 'vue';
    import Component from "vue-class-component";
    import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
    import {OrganizationDetailsModel, RoleModel, SpecialityLightModel, SysAdminOrganizationsClient, SysAdminSpecialitiesClient, UpdateOrganizationRolesModel} from "@/api-client";
    import {Prop} from "vue-property-decorator";
    import {isNullOrWhiteSpace} from "@/extensions/string.helpers";
    import {BModal} from "bootstrap-vue";
    import toasts from "@/components/Toasts";
    import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
    import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
    import OrganizationDetails from "@/features/organizations/views/OrganizationDetails.vue";
    import { OrganizationActions, OrganizationsClient } from '@/api-client';
    import {NavigationGuardNext} from "vue-router/types/router";
    import PopoverInfo from "@/components/PopoverInfo.vue";
    import Loader from '@/features/shared/components/Loader';
    import ConfirmModal from "@/features/shared/components/ConfirmModal.vue";
    import { ResultError } from '@/api-client.extensions';

    interface Props {
        id: string;
        preLoadedOrganization: OrganizationDetailsModel;
        preLoadedRoles: RoleModel[];
        availableActions: OrganizationActions;
        availableSpecialities: SpecialityLightModel[]
    }

    @Component({
        components:{
            SectionPageHeading,
            AdministrationDetailviewLayout,
            HeadingGroup,
            OrganizationDetails,
            PopoverInfo,
            ConfirmModal
        },
        beforeRouteEnter(to, from, next: NavigationGuardNext<AdminOrganization>) {
            const props = to.meta!.props as Props;
            props.id = to.params.id;
            Promise.all([
                new SysAdminOrganizationsClient().details(props.id),
                new SysAdminOrganizationsClient().roles(props.id),
                new OrganizationsClient().availableActions(props.id),
                new SysAdminSpecialitiesClient().all(null)
            ]).then(values => {
                props.preLoadedOrganization = values[0];
                props.preLoadedRoles = values[1];
                props.availableActions = values[2];
                props.availableSpecialities = values[3];
                next();
            }).catch(e => next(e))
        }
    })
    export default class AdminOrganization extends Vue implements Props {
        @Prop({required: true})
        id!: string;
        @Prop({required: true})
        preLoadedOrganization!: OrganizationDetailsModel;
        @Prop({required: true})
        preLoadedRoles!: RoleModel[];
        @Prop({required: true})
        availableActions!: OrganizationActions;
        @Prop({required: true})
        availableSpecialities!: SpecialityLightModel[];

        organization = this.preLoadedOrganization;
        selectedRoles = (this.organization.roles || []).map(r => this.preLoadedRoles.filter(role => role.value === r.value)[0]);
        updateLoader = new Loader();

        $refs!:{
            modal: BModal;
            organizationDetails: OrganizationDetails;
        }

        get types(){
            const types = (this.organization.types || []).map(x => x.name).join(", ");
            return isNullOrWhiteSpace(types) ? "-" : types;
        }

        get roles(){
            const roles = (this.organization.roles || []).map(r => r.name).join(", ");
            return isNullOrWhiteSpace(roles) ? "-" : roles;
        }

        get isValidRegisterCode(){
            return this.preLoadedOrganization.registerCode?.length == 8;
        }

        openEditRolesModal(){
            this.selectedRoles = (this.organization.roles || []).map(r => this.preLoadedRoles.filter(role => role.value === r.value)[0]);
            this.$refs.modal.show();
        }

        async updateRoles(){
            const model = new UpdateOrganizationRolesModel({roles: this.selectedRoles.map(r => r.value)});
            await new SysAdminOrganizationsClient().updateRoles(model, this.id);
            toasts.success(this.$ls.Administration_Organization_Toast_Success_RolesChanged);
            this.organization.roles = this.selectedRoles;
            this.$refs.modal.hide();
        }

        async updateFromArireg(){
            await new SysAdminOrganizationsClient().setLoader(this.updateLoader).updateFromArireg(this.organization.id)
                .then(() => {
                    toasts.success(this.$ls.Organization_Toast_Success_OrganizationDetailsUpdated);
                }).catch(error => {
                    const resultError = error as ResultError;
                    resultError.messagesFlattened.forEach( m => {
                        toasts.failure(m);
                    });
                });
            this.$refs.organizationDetails.reloadOrganization();
        }
    }
